import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { CONTRACT_ROLES, compileBlocks, type Language } from "@talentkontor/domain";
import { api } from "../../../../data";
import { useStaffWorkspace } from "../../../../app/session";
import { ErrorState, Loading } from "../../../../components/States";
import { Dialog, PageHead, useAction } from "../../../../components/ui";
import { currentLang, t, sprachName } from "../../../../lib/sprache";
import { date } from "../../../../lib/format";
import { VertragPapier } from "../Papier";
import { exampleValues, renderDraft, vorlagenBeispiel } from "../pruefen";
import { loadContractSettings, loadOrgDaten } from "../daten";
import { KATEGORIEN, kategorieLabel, loadKlauseln, type KlauselZeile } from "./bibliothek";
import "./baukasten.css";

/**
 * Klausel-Bibliothek (Schritt 4, VB-15): CreatorOffice-Klauseln und eigene Klauseln der Agentur, je Sprache und Kategorie.
 * Eingefügt wird im Baukasten über „/“ → „Klausel aus der Bibliothek“; eigene Klauseln entstehen dort über „Als Klausel
 * speichern“. Hier: ansehen, umbenennen, Kategorie, archivieren, löschen.
 */
export function Klauseln() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const darf = ws.can("edit_templates");
  const q = useQuery({ queryKey: ["contract_clauses", orgId], queryFn: () => loadKlauseln(api, orgId) });
  const [lang, setLang] = useState<Language>(currentLang() === "en" ? "en" : "de");
  const [suche, setSuche] = useState("");
  const [archiv, setArchiv] = useState(false);
  const [ansehen, setAnsehen] = useState<KlauselZeile | null>(null);
  const [bearbeiten, setBearbeiten] = useState<KlauselZeile | null>(null);
  const { busy, error, attempt } = useAction();
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const s = suche.trim().toLowerCase();
  const liste = q.data.filter((k) => k.language === lang && (archiv || !k.archived_at) && (!s || k.title.toLowerCase().includes(s) || kategorieLabel(k.category).toLowerCase().includes(s)));
  const gruppen = KATEGORIEN.map((c) => ({ c, rows: liste.filter((k) => k.category === c) })).filter((g) => g.rows.length);

  return (
    <main id="main" className="content bk">
      <PageHead title={t("Klausel-Bibliothek", "Clause library")} eyebrow={t("Verträge · Vorlagen", "Contracts · Templates")}
        text={t("Wiederverwendbare Klauseln für eure Vorlagen. Eine Vorlage speichert beim Einfügen die aktuelle Version; kommt später eine neuere, zeigt der Baukasten „neuere Fassung verfügbar“. Klauseln sind technische Textbausteine und stellen keine Rechtsberatung dar.", "Reusable clauses for your templates. A template stores the current version when inserting; if a newer one comes later, the builder shows “newer version available”. Clauses are technical text blocks and do not constitute legal advice.")}>
        <Link className="btn" to="/agentur/vertraege/vorlagen">{t("Zu den Vorlagen", "To the templates")}</Link>
      </PageHead>
      <div className="toolbar">
        <span role="group" aria-label={t("Sprache", "Language")}>{(["de", "en"] as Language[]).map((l) => <button key={l} type="button" aria-pressed={lang === l} onClick={() => setLang(l)}>{sprachName(l)}</button>)}</span>
        <input aria-label={t("Klausel suchen", "Search clause")} placeholder={t("Suchen …", "Search …")} value={suche} onChange={(e) => setSuche(e.target.value)} />
        <label className="row"><input type="checkbox" checked={archiv} onChange={(e) => setArchiv(e.target.checked)} />{t("Archivierte zeigen", "Show archived")}</label>
      </div>
      <p className="notice">{t("Eigene Klausel anlegen: im Baukasten einen Baustein wählen und unter „Eigenschaften“ auf „Als Klausel speichern“ klicken.", "Create your own clause: select a block in the builder and click “Save as clause” under “Properties”.")}</p>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {gruppen.length === 0 && <p>{t("Keine Klausel passt.", "No clause matches.")}</p>}
      {gruppen.map((g) => (
        <section key={g.c} className="panel stack" aria-label={kategorieLabel(g.c)}>
          <h3>{kategorieLabel(g.c)}</h3>
          {g.rows.map((k) => (
            <div key={`${k.clauseId}-${k.language}`} className="task">
              <div>
                <strong>{k.title}</strong>
                <p>{k.eigen ? t("eigene Klausel", "own clause") : t("CreatorOffice-Klausel", "CreatorOffice clause")} · {t(`Version ${k.version}`, `version ${k.version}`)}
                  {k.updated_at ? t(` · geändert ${date(k.updated_at, ws.org.timezone)}`, ` · changed ${date(k.updated_at, ws.org.timezone)}`) : ""}{k.archived_at ? t(" · archiviert", " · archived") : ""}</p>
              </div>
              <div className="row">
                <button type="button" onClick={() => setAnsehen(k)}>{t("Ansehen", "View")}</button>
                {darf && k.eigen && k.id && (
                  <>
                    <button type="button" onClick={() => setBearbeiten(k)}>{t("Umbenennen", "Rename")}</button>
                    <button type="button" disabled={!!busy} onClick={() => void attempt(`archiv-${k.id}`, () => api.update("contract_clauses", [["id", "eq", k.id!]], { archived_at: k.archived_at ? null : new Date().toISOString() }, { columns: "id" }),
                      k.archived_at ? t("Klausel wiederhergestellt.", "Clause restored.") : t("Klausel archiviert. Vorlagen, die sie nutzen, behalten ihren Stand.", "Clause archived. Templates using it keep their version."))}>{k.archived_at ? t("Wiederherstellen", "Restore") : t("Archivieren", "Archive")}</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>
      ))}
      <Ansicht klausel={ansehen} onClose={() => setAnsehen(null)} orgId={orgId} />
      <Umbenennen klausel={bearbeiten} onClose={() => setBearbeiten(null)} />
    </main>
  );
}

function Ansicht({ klausel, onClose, orgId }: { klausel: KlauselZeile | null; onClose: () => void; orgId: string }) {
  const ws = useStaffWorkspace();
  const settings = useQuery({ queryKey: ["contract_settings", orgId], queryFn: () => loadContractSettings(api, orgId) });
  const org = useQuery({ queryKey: ["org_daten", orgId], queryFn: () => loadOrgDaten(api, orgId) });
  let html: string | null = null;
  if (klausel) {
    const c = compileBlocks(klausel.blocks, { language: klausel.language });
    const b = vorlagenBeispiel(org.data, klausel.language, settings.data?.number_format ?? "V-{NNNN}", new Date().getFullYear());
    html = renderDraft({ body: c.bodyHtml, annex: null, defs: c.variables, values: exampleValues(c.variables), context: b.context, locale: b.locale, currency: b.currency }).html;
  }
  return (
    <Dialog open={!!klausel} onClose={onClose} title={klausel?.title ?? ""} wide>
      {klausel && <VertragPapier logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={html} lang={klausel.language} eyebrow={t("Mit Beispielwerten", "With sample values")} marks={CONTRACT_ROLES.map((r) => ({ role: r, signed: false }))} />}
    </Dialog>
  );
}

function Umbenennen({ klausel, onClose }: { klausel: KlauselZeile | null; onClose: () => void }) {
  return (
    <Dialog open={!!klausel} onClose={onClose} title={t("Klausel umbenennen", "Rename clause")}>
      {klausel && <UmbenennenForm key={klausel.clauseId} k={klausel} onDone={onClose} />}
    </Dialog>
  );
}

function UmbenennenForm({ k, onDone }: { k: KlauselZeile; onDone: () => void }) {
  const [title, setTitle] = useState(k.title);
  const [category, setCategory] = useState(k.category);
  const { busy, error, attempt } = useAction();
  return (
    <form className="stack" onSubmit={async (e) => {
      e.preventDefault();
      if (await attempt("umbenennen", () => api.update("contract_clauses", [["id", "eq", k.id!]], { title: title.trim(), category }, { columns: "id" }), t("Gespeichert.", "Saved."))) onDone();
    }}>
      <label>{t("Titel", "Title")}<input required minLength={2} maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label>{t("Kategorie", "Category")}<select value={category} onChange={(e) => setCategory(e.target.value)}>{KATEGORIEN.map((c) => <option key={c} value={c}>{kategorieLabel(c)}</option>)}</select></label>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row"><button className="primary" type="submit" disabled={!!busy || title.trim().length < 2}>{t("Speichern", "Save")}</button></div>
    </form>
  );
}
